import { CommonModule } from "@angular/common";
import {
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerModule,
  NgbDatepickerI18n,
  NgbInputDatepicker,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { TranslationService } from "src/app/i18n";
import {
  CustomDateParserFormatter,
  CustomDatepickerI18n,
  getDatepickerYearOffset,
  LocaleAwareCalendar,
  serializeNgbDate,
  shiftNgbDateYear,
  toNgbDate,
} from "src/utils/datapicker-format";
import { LabelMode } from "src/utils/label-mode.enum";
import { GenerateRandom } from "src/utils/random";
import { KeeniconComponent } from "../keenicon/keenicon.component";

interface DateRangeOutputValue {
  startValue?: string;
  endValue?: string;
}

@Component({
  selector: "app-daterange-picker",
  templateUrl: "./daterange-picker.component.html",
  styleUrls: ["./daterange-picker.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    TranslateModule,
    KeeniconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DaterangePickerComponent),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbCalendar, useClass: LocaleAwareCalendar },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
})
export class DaterangePickerComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild("d1") startDatepicker?: NgbInputDatepicker;
  @ViewChild("d2") endDatepicker?: NgbInputDatepicker;

  @Input() name = GenerateRandom(6);
  @Input() label: string;
  @Input() placeholder = "";
  @Input() classes: string;
  @Input() inputClass: string;
  @Input() minYear = 1900;
  @Input() beYear = 543;
  @Input() clearable = false;
  @Input() required = false;
  @Input() errorMessage = "VALIDATOR.REQUIRED";

  form: FormGroup<{
    startValue: FormControl<NgbDate | null>;
    endValue: FormControl<NgbDate | null>;
  }> = inject(FormBuilder).group({
    startValue: new FormControl<NgbDate | null>(null),
    endValue: new FormControl<NgbDate | null>(null),
  });
  isError = false;
  minPDate?: NgbDateStruct;
  maxPDate?: NgbDateStruct;
  LabelMode = LabelMode;
  mode: LabelMode = LabelMode.DEFAULT;

  private readonly subscriptions = new Subscription();
  private readonly translationService = inject(TranslationService);
  private readonly translateService = inject(TranslateService);
  private minDateValue?: Date;
  private maxDateValue?: Date;
  private yearOffset: number;

  constructor() {
    this.yearOffset = getDatepickerYearOffset(
      this.translateService.currentLang,
      this.translationService.getSelectedLanguage(),
      this.beYear
    );
  }

  @Input()
  set isDisabled(value: boolean) {
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Input()
  set error(value: any) {
    this.isError = value != null && value !== false;
  }

  @Input() set minDate(value: any) {
    this.minDateValue = value != null ? new Date(value) : undefined;
    this.updateDateBounds();
  }

  @Input() set maxDate(value: any) {
    this.maxDateValue = value != null ? new Date(value) : undefined;
    this.updateDateBounds();
  }

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  onChange = (value: DateRangeOutputValue) => {};
  onTouched = (value: string) => {};

  ngOnInit(): void {
    this.yearOffset = getDatepickerYearOffset(
      this.translateService.currentLang,
      this.translationService.getSelectedLanguage(),
      this.beYear
    );
    this.updateDateBounds();

    this.subscriptions.add(
      this.form.valueChanges.subscribe((value) => {
        const output: DateRangeOutputValue = {};

        if (
          value.startValue?.year != null &&
          value.startValue.year - this.yearOffset >= this.minYear
        ) {
          output.startValue = serializeNgbDate(
            value.startValue,
            this.yearOffset
          );
        }

        if (
          value.endValue?.year != null &&
          value.endValue.year - this.yearOffset >= this.minYear
        ) {
          output.endValue = serializeNgbDate(value.endValue, this.yearOffset);
        }

        this.onChange(output);
      })
    );

    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        const previousOffset = this.yearOffset;
        this.yearOffset = getDatepickerYearOffset(
          this.translateService.currentLang,
          this.translationService.getSelectedLanguage(),
          this.beYear
        );
        this.updateDateBounds();

        const value = this.form.getRawValue();
        const startValue = shiftNgbDateYear(
          value.startValue,
          previousOffset,
          this.yearOffset
        );
        const endValue = shiftNgbDateYear(
          value.endValue,
          previousOffset,
          this.yearOffset
        );

        this.form.patchValue({ startValue, endValue }, { emitEvent: false });

        if (this.startDatepicker?.isOpen() === true) {
          this.startDatepicker.navigateTo(startValue ?? undefined);
        }
        if (this.endDatepicker?.isOpen() === true) {
          this.endDatepicker.navigateTo(endValue ?? undefined);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  writeValue(obj: any): void {
    if (obj?.startValue == null || obj.endValue == null) {
      this.form.reset(
        {
          startValue: null,
          endValue: null,
        },
        { emitEvent: false }
      );
      return;
    }

    this.form.patchValue(
      {
        startValue: toNgbDate(new Date(obj.startValue), this.yearOffset),
        endValue: toNgbDate(new Date(obj.endValue), this.yearOffset),
      },
      { emitEvent: false }
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  private updateDateBounds(): void {
    this.minPDate = this.minDateValue
      ? toNgbDate(this.minDateValue, this.yearOffset)
      : undefined;
    this.maxPDate = this.maxDateValue
      ? toNgbDate(this.maxDateValue, this.yearOffset)
      : undefined;
  }
}
